import { CircleCheck, CopyCheck, TriangleAlert } from 'lucide-react'

import { Button } from '../../../ui/Button/Button'
import type { BookingCheck, StaffResolution } from '../booking-checks-contracts'
import styles from './BookingCheckCard.module.css'

const channelLabel: Record<BookingCheck['sourceChannel'], string> = {
  VOICE: 'by phone',
  SMS: 'by text',
  DASHBOARD: 'in the Dashboard',
}

const reasonSentence: Partial<Record<BookingCheck['reason'], string>> = {
  ONLINE_NOT_WRITTEN:
    'An hour with the practice system connected hasn’t been enough for it to show up.',
  APPOINTMENT_SOON: 'The appointment is coming up and it hasn’t shown up yet.',
  APPOINTMENT_NOT_FOUND:
    'NexHealth can no longer find this booking, so it may not arrive on its own.',
  STATUS_UNAVAILABLE:
    'Flio can’t currently read whether it has arrived, so please check.',
}

const resolvedLabel: Record<string, string> = {
  FOUND_IN_PMS: 'Found in the practice system by staff',
  ADDED_TO_PMS: 'Added to the practice system by staff',
  HANDLED_WITH_PATIENT: 'Handled with the patient by staff',
  DUPLICATE_HANDLED: 'Duplicate handled by staff',
  REACHED_PMS: 'Reached the practice system on its own',
  BOOKING_CANCELLED: 'Booking was cancelled',
}

export function formatCheckTime(
  timestampMs: number | null | undefined,
  timeZone: string | null | undefined
) {
  if (!timestampMs) return null
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timeZone ?? undefined,
    }).format(timestampMs)
  } catch {
    return new Date(timestampMs).toLocaleString()
  }
}

/**
 * One Booking check. Recording what staff did never confirms the booking:
 * the check closes itself when the practice system has it.
 */
export function BookingCheckCard({
  check,
  onResolve,
  pending = null,
  failed = false,
  showAppointment = true,
}: {
  check: BookingCheck
  onResolve?: (resolution: StaffResolution) => void
  pending?: StaffResolution | null
  failed?: boolean
  showAppointment?: boolean
}) {
  const appointment = check.appointment
  const when = formatCheckTime(
    appointment?.scheduledAtMs,
    appointment?.clinicTimeZone
  )
  const who = appointment?.patientDisplayName ?? 'A patient'
  const details = [
    appointment?.appointmentTypeName,
    appointment?.providerName,
    appointment?.operatoryName,
  ]
    .filter(Boolean)
    .join(' · ')

  if (check.status === 'RESOLVED' && check.resolution) {
    return (
      <div className={styles.card} data-tone="resolved">
        <CircleCheck size={16} aria-hidden="true" />
        <div className={styles.copy}>
          <strong>{resolvedLabel[check.resolution.resolutionType]}</strong>
          <span>
            {formatCheckTime(
              check.resolution.resolvedAtMs,
              appointment?.clinicTimeZone
            )}
          </span>
        </div>
      </div>
    )
  }

  const duplicate = check.kind === 'POSSIBLE_DUPLICATE'
  const actions: Array<{ type: StaffResolution; label: string }> = duplicate
    ? [{ type: 'DUPLICATE_HANDLED', label: 'Duplicate handled' }]
    : [
        { type: 'FOUND_IN_PMS', label: 'It’s already there' },
        { type: 'ADDED_TO_PMS', label: 'Added to practice system' },
        { type: 'HANDLED_WITH_PATIENT', label: 'Handled with the patient' },
      ]

  return (
    <div
      className={styles.card}
      data-tone="warning"
      role="group"
      aria-label={
        duplicate
          ? 'Possible duplicate booking'
          : 'Booking to check in the practice system'
      }
    >
      {duplicate ? (
        <CopyCheck size={16} aria-hidden="true" />
      ) : (
        <TriangleAlert size={16} aria-hidden="true" />
      )}
      <div className={styles.copy}>
        <strong>
          {duplicate
            ? 'This booking may now be in your practice system twice'
            : 'Check this booking in your practice system'}
        </strong>
        {showAppointment ? (
          <span className={styles.subject}>
            {who}
            {when ? ` · ${when}` : ''}
            {details ? ` · ${details}` : ''}
          </span>
        ) : null}
        {duplicate ? (
          <p>
            Flio’s original booking has now reached your practice system after
            it was handled here. Check whether the appointment is there twice;
            if it is, remove one copy there.
          </p>
        ) : (
          <p>
            Flio booked this {channelLabel[check.sourceChannel]}, but it hasn’t
            yet been seen in your practice system.{' '}
            {reasonSentence[check.reason] ?? ''} First look for the appointment
            {check.newPatient ? ' and the new patient' : ''} there; it may
            already be there. Add it only if it isn’t, or contact the patient,
            then record what you did. Flio keeps checking and tells you if the
            original arrives later.
          </p>
        )}
        {onResolve ? (
          <div className={styles.actions}>
            {actions.map((action, index) => (
              <Button
                key={action.type}
                type="button"
                variant={index === 0 ? 'primary' : 'secondary'}
                disabled={pending !== null}
                aria-busy={pending === action.type}
                onClick={() => onResolve(action.type)}
              >
                {pending === action.type ? 'Saving…' : action.label}
              </Button>
            ))}
          </div>
        ) : null}
        {failed ? (
          <p className={styles.error} role="alert">
            That couldn’t be saved. Refresh and try again.
          </p>
        ) : null}
      </div>
    </div>
  )
}
